import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const InteractiveMap = ({
  areaName = "Lakewood Ranch",
  cursor,
  cursorDrawTrigger,
  bluePolygons,
  setBluePolygons,
  redPolygons,
  setRedPolygons,
  onGenerateReport,
  setRemoveMarkerAtIndexRef,
  parcelBoundaries = [],
  selectedParcelIndex = null,
  setRemoveMarkersByAPNsRef,
  mapApiRef,
  heatmapType,
  reportInProgress,
  reportProgress,
  liveAPNs,
  setLiveAPNs,
  selectedApns,        
  setSelectedApns,
  onSearchError,
}) => {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const [mode, setMode] = useState('select'); // 'select' | 'remove'
  const removeMode = mode === 'remove';
  const [heatmapReady, setHeatmapReady] = useState(false);
  const sseRef = useRef(null);
  const parcelMarkers = useRef({});
  const heatmapOverlayRef = useRef(null);
  const boundaryPolygonsRef = useRef([]);
  const nextMarkerIndexRef = useRef(0);
  const didSetInitialPoint = useRef(false);
  const heatmapSourceRef = useRef(null);
  const renderingRef = useRef(false);
  const removeModeRef = useRef(false);
  const [usernameInput, setUsernameInput] = useState("");
  // const [selectedApns, setSelectedApns] = useState([]);


  useEffect(() => { removeModeRef.current = removeMode; }, [removeMode]);
  const heatmapTypeLayerMap = {
  "Wetland Type": "wetlands_wetland_type",
  "System Name": "wetlands_wetland_system",
  "Class Name": "wetlands_wetland_class",
  "Modifier": "wetlands_wetland_modifier1",
  "Elevation":"elevation",
  "Soil":"soil_texture_class",
  "Flood Zone Type": "flood_zones_zone_type"
  // Add other mappings as needed, matching the backend API layer types
};

const heatmapTypeToLayerName = (t) => {
  if (t === "Elevation") return "Elevation";
  if (t.startsWith("Soil_") || t === "Clay" || t === "Sand") return "Soil";
  if (t === "Flood Zone Type") return "FEMA";
  return "Wetlands";
};


const zoomingRef = useRef(false);

function isValidBounds(b) {
   // Expect [[south, west], [north, east]], accept numeric strings.
   const toNum = (x) => Number(x);
   const isNum = (x) => Number.isFinite(toNum(x));
   if (!Array.isArray(b) || b.length !== 2) return false;
   if (!Array.isArray(b[0]) || !Array.isArray(b[1])) return false;
   if (b[0].length !== 2 || b[1].length !== 2) return false;
   if (![b[0][0], b[0][1], b[1][0], b[1][1]].every(isNum)) return false;
  return toNum(b[0][0]) < toNum(b[1][0]); // south < north
 }

function addHeatmapOverlaySafe(url, rawBounds) {
  if (!leafletMap.current) return;
  if (!isValidBounds(rawBounds)) {
    console.warn("Skipping overlay: invalid bounds", rawBounds);
    if (heatmapOverlayRef.current && leafletMap.current?.hasLayer(heatmapOverlayRef.current)) {
      leafletMap.current.removeLayer(heatmapOverlayRef.current);
      heatmapOverlayRef.current = null;
    }
    window.dispatchEvent(new CustomEvent('heatmap:not_found'));
    return;
  }
 const bounds = L.latLngBounds(
   [Number(rawBounds[0][0]), Number(rawBounds[0][1])],
    [Number(rawBounds[1][0]), Number(rawBounds[1][1])]
  );

  // Remove any existing overlay first (prevents race during anim)
  if (heatmapOverlayRef.current && leafletMap.current.hasLayer(heatmapOverlayRef.current)) {
    leafletMap.current.removeLayer(heatmapOverlayRef.current);
  }

  heatmapOverlayRef.current = L.imageOverlay(url, bounds, { opacity: 0.6 });
  heatmapOverlayRef.current.addTo(leafletMap.current);
}

const handleParcelClick = useCallback((e) => {
  if (removeMode) return;
  console.log("📍 handleParcelClick fired");
  const { lat, lng } = e.latlng;
  const latlonString = `${lat},${lng}`;
  console.log("🟦 Map clicked to add parcel at:", latlonString);

  fetch(`/api/geocode?area_name=${latlonString}`)
    .then(res => res.json())
    .then(data => {
      if (!data || !data.centroid || data.centroid.length !== 2) {
      console.warn("⚠️ No valid centroid returned from /geocode:", data);}
      const [lon, lat] = data.centroid;
      const apn = data.apn
      const address = data.address
      const boundary = data.boundary
      
      // setSelectedApns(prev => [...prev, apn]);    
      // setBluePolygons(prev => [...prev, [lon, lat]]);
      // window.dispatchEvent(new CustomEvent("parcelAdded", {
      //   detail: { apn, address, boundary }
      // }));

        setSelectedApns(prev => {
        if (prev.includes(apn)) {
          console.log("Skipping duplicate parcel:", apn);
          return prev;
        }

        setBluePolygons(bp => [...bp, [lon, lat]]);
        window.dispatchEvent(new CustomEvent("parcelAdded", {
          detail: { apn, address, boundary }
        }));
        return [...prev, apn];
      });
    })
    .catch(err => console.error("❌ Parcel fetch failed:", err));
}, [setBluePolygons]);

const drawBoundaryPolygons = (boundaries) => {
  if (!leafletMap.current) return;

  // Remove old polygons
  boundaryPolygonsRef.current.forEach(polygon => {
    if (polygon && leafletMap.current.hasLayer(polygon)) {
      leafletMap.current.removeLayer(polygon);
    }
  });
  boundaryPolygonsRef.current = [];

  boundaries.forEach((geoBoundary,i) => {
    if (!geoBoundary || !Array.isArray(geoBoundary)) return;

    // Normalize to array of rings
    const rings = Array.isArray(geoBoundary[0][0]) ? geoBoundary : [geoBoundary];

    // Convert to Leaflet [lat, lng] format with validation
    const latlngBoundary = rings.map(ring => {
      if (!Array.isArray(ring)) return [];

      return ring
        .map(coord => {
          const [a, b] = coord;
          if (typeof a !== 'number' || typeof b !== 'number') return null;

          // Swap [lon, lat] to [lat, lon] if needed
          return Math.abs(a) > 90 ? [b, a] : [a, b];
        })
        .filter(Boolean);
    });

    // Filter out empty or invalid rings
    if (!latlngBoundary[0] || latlngBoundary[0].length < 3) {
      console.warn("❌ Skipping invalid polygon:", latlngBoundary);
      return;
    }

    let isSelected = false;

    if (!reportInProgress) {
      // Before analysis: all selected parcels are blue
      isSelected = true;
    } else {
      // After analysis: only the dropdown-selected parcel is blue
      isSelected = i === selectedParcelIndex;
    }
    console.log("selectedParcelIndex", selectedParcelIndex)
    console.log("isSelected", isSelected)

        try {
      const polygon = L.polygon(latlngBoundary, {
    color: isSelected ? 'blue' : '#222222', 
    fillColor: isSelected ? 'blue' : '#555555', 
    opacity: isSelected ? 1 : 1,
    fillOpacity: isSelected ? 0.2 : 0.3,
    weight: isSelected ? 3 : 3,
    dashArray: isSelected ? '10,5' : '10,5',
      }).addTo(leafletMap.current);

      boundaryPolygonsRef.current.push(polygon);

      if (removeMode) {
        polygon.on("mouseover", () => {
          polygon.setStyle({ color: "red", fillColor: "red", weight: 3, fillOpacity: 0.3 });
        });
        polygon.on("mouseout", () => {
          polygon.setStyle({
            color: isSelected ? 'blue' : '#999999',
            fillColor: isSelected ? 'blue' : '#cccccc',
            opacity: isSelected ? 1 : 0.8,
            fillOpacity: 0.2,
            weight: isSelected ? 3 : 1.5,
            dashArray: isSelected ? '10,5' : '2,4',
          });
        });
        polygon.on("click", () => {
            if (reportProgress) {
                alert("Sorry! I am in the process of analyzing your current site data. Please wait to remove parcels until the analysis is complete!");
                return;
              }
          console.log("🗑 Removing parcel at index:", i);
          window.dispatchEvent(new CustomEvent("parcelRemoved", { detail: { index: i } }));
        });
      }
    } catch (err) {
      console.error("❌ Error creating polygon:", err);
    }
  });
};

// const BACKEND_BASE_URL = "http://127.0.0.1:2500";
const BACKEND_BASE_URL = "/api"//"http://34.134.175.200:2600/";
function makeHeatmapRequestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function startHeatmapSSE(latlonList, layerName, layerType) {
  clearHeatmapOverlay();
  window.setIsGeneratingHeatmaps(true);
  const loader = document.getElementById("heatmap-loader");
  if (loader) loader.innerText = "Starting heatmap generation...";

  const requestId = makeHeatmapRequestId();
  const qs = new URLSearchParams({
    latlon_list: JSON.stringify(latlonList),
    layer: layerName,
    layer_type: layerType,
    request_id: requestId,
  }).toString();

  const source = new EventSource(`/api/fetch_heatmaps_latlon?${qs}`);
  heatmapSourceRef.current = source;

  source.onmessage = (event) => {
    if (!event.data) return;

    let parsed;
    try { parsed = JSON.parse(event.data); } catch { return; }
    const { type, res } = parsed;

    if (type === "progress_heat") {
      const loader = document.getElementById("heatmap-loader");
      if (loader) loader.innerText = res;
    }

    if (type === "output") {
      const loader = document.getElementById("heatmap-loader");
      if (loader) loader.innerText = "✅ Heatmaps generation complete";

      const data = res;
      if (data.imageUrl && data.bounds) {
        const overlayUrl = BACKEND_BASE_URL + data.imageUrl + `?v=${Date.now()}`;
        console.log("overlayUrloverlayUrl", overlayUrl)
        const scheduleAdd = () => addHeatmapOverlaySafe(overlayUrl, data.bounds);
        if (zoomingRef.current) leafletMap.current.once("zoomend", scheduleAdd);
        else scheduleAdd();
      }
      try { source.close(); } catch {}
      heatmapSourceRef.current = null;
      renderingRef.current = false;
      setHeatmapReady(true);
      window.setIsGeneratingHeatmaps?.(false);
      window.dispatchEvent(new CustomEvent("heatmap:ready", {
        detail: { requestId: data.requestId || requestId }
      }));
    }
  };

  source.onerror = (err) => {
    console.error("Heatmap SSE error:", err);
    try { source.close(); } catch {}
    heatmapSourceRef.current = null;
    renderingRef.current = false;
    window.setIsGeneratingHeatmaps?.(false);

    window.dispatchEvent(new CustomEvent("heatmap:not_found", {
      detail: { layer: layerName, layerType }
    }));
  };
}

function formatHeatmapType(heatmapTypeName) {
  if (typeof heatmapTypeName === "string" && heatmapTypeName.startsWith("Soil_")) {
    return "Soil";
  }
  return heatmapTypeName;
}

const renderHeatmapOverlay = async () => {
  setHeatmapReady(false);


  if (renderingRef.current) return;
  renderingRef.current = true;
  if (heatmapSourceRef.current) {
    try { heatmapSourceRef.current.close(); } catch {}
    heatmapSourceRef.current = null;
  }


  console.log("🔥 renderHeatmapOverlay triggered");
  console.log("🟦 bluePolygons inside heatmap:", bluePolygons);
    if (!leafletMap.current) {
    console.warn("🧭 Leaflet map not ready");
    return;
  }
  if (!leafletMap.current || bluePolygons.length === 0) return;

  // const latlonList = bluePolygons.map(([lon, lat]) => [lat, lon]);
  // --- Handle "All Parcels Combined" without touching the single-parcel snippet ---

  if (selectedParcelIndex == null) {
    if (!leafletMap.current || !bluePolygons.length || !heatmapTypeLayerMap[formatHeatmapType(heatmapType)]) {
      clearHeatmapOverlay();
      renderingRef.current = false;
      return;
    }

    const layerType = heatmapTypeLayerMap[formatHeatmapType(heatmapType)];
    // const layerName = heatmapType === "Elevation" ? "Elevation" : "Wetlands";
    const layerName = heatmapTypeToLayerName(heatmapType);
    const allLatLon = bluePolygons.map(([lon, lat]) => [lat, lon]); // [lat, lon]

    startHeatmapSSE(allLatLon, layerName, layerType);
    // Important: stop here so we don't fall through to the single-parcel path.
    return;
  }

  const latlonList =
  typeof selectedParcelIndex === "number" &&
  bluePolygons[selectedParcelIndex]
    ? [
        [bluePolygons[selectedParcelIndex][1], bluePolygons[selectedParcelIndex][0]]
      ] // [lat, lon]
    : [];
  
  if (!latlonList.length || !heatmapTypeLayerMap[formatHeatmapType(heatmapType)]) {
    clearHeatmapOverlay();
    return;
  }
  const layerType = heatmapTypeLayerMap[formatHeatmapType(heatmapType)]
  const layerName = heatmapTypeToLayerName(heatmapType);
  // const layerName = heatmapType === 'Elevation' ? 'Elevation' : 'Wetlands';
 let depthLabel = null;
 console.log(heatmapType);
if (heatmapType.startsWith("Soil_")) {
  const parts = heatmapType.split("_");
  depthLabel = parts[1];     // "0-30cm" or whatever comes after "_"
}

const payload = {
  latlon_list: latlonList,        // e.g. [[lat, lon]]
  layer: layerName,
  layer_type: layerType,          // e.g. "wetlands_wetland_type"
  depth_label: depthLabel
};
  window.setIsGeneratingHeatmaps(true);
  const loader = document.getElementById("heatmap-loader");
    if (loader) {
      loader.innerText = "Starting heatmap generation...";
    }
  
const qs = new URLSearchParams({
  latlon_list: JSON.stringify(latlonList),
  layer: layerName,
  layer_type: layerType,
  depth_label: depthLabel,
  request_id: makeHeatmapRequestId()
}).toString();

const source = new EventSource(`/api/fetch_heatmaps_latlon?${qs}`);;
heatmapSourceRef.current = source;

source.onmessage = (event) => {
  if (!event.data) return;

  let parsed;
  try {
    parsed = JSON.parse(event.data);
  } catch (e) {
    console.warn('Failed to parse heatmap SSE message', e);
    return;
  }

  const { type, res } = parsed;

  
  if (type === 'progress_heat') {
    console.log('progress_heatprogress_heat');
    const loader = document.getElementById('heatmap-loader');
    if (loader) {
      loader.innerText = res; // overwrite with latest
    }
  }
  console.log("📨 SSE Message received:", parsed);

//   if (type === "heatmap_empty") {
//   // Reuse your existing UI wiring
//   window.dispatchEvent(new CustomEvent("heatmap:not_found"));

//   // clean up state/spinner like you do on success/error
//   try { source.close(); } catch {}
//   heatmapSourceRef.current = null;
//   renderingRef.current = false;
//   window.setIsGeneratingHeatmaps?.(false);
//   console.log("no heatmaps!!!!");

// }

  if (type === 'output') {
    const loader = document.getElementById("heatmap-loader");
    if (loader) {
      loader.innerText = '✅ Heatmaps generation complete'; // overwrite with latest
      console.log("inside output loader")
    }
    const data = res;
    console.log("✅ Output message data:", data);
    console.log("🖼️ Overlay image URL:", BACKEND_BASE_URL + data.imageUrl);
    console.log("📦 Bounds:", data.bounds);
    if (data.imageUrl && data.bounds) {
       const overlayUrl = BACKEND_BASE_URL + data.imageUrl + `?v=${Date.now()}`;
       console.log("overlayUrloverlayUrl", overlayUrl)
       const scheduleAdd = () => addHeatmapOverlaySafe(overlayUrl, data.bounds);
       if (zoomingRef.current) {
         // Wait until the current zoom animation ends.
         leafletMap.current.once('zoomend', scheduleAdd);
       } else {
         scheduleAdd();
       }
     }
    try { source.close(); } catch {}
    heatmapSourceRef.current = null;
    renderingRef.current = false;
    setHeatmapReady(true);  
    window.setIsGeneratingHeatmaps?.(false);
    window.dispatchEvent(new CustomEvent("heatmap:ready", {
      detail: { requestId: data.requestId }
    }));
  }
};

source.onerror = (err) => {
  console.error('Heatmap SSE error:', err);
      try { source.close(); } catch {}
    heatmapSourceRef.current = null;
    renderingRef.current = false;
  window.setIsGeneratingHeatmaps?.(false);

  clearHeatmapOverlay();
  window.dispatchEvent(new CustomEvent('heatmap:not_found', {
  detail: {
    layer: layerName,
    layerType,                  // from your scope
    selectedParcelIndex         // from your props
  }
  }));

};
};


const fitMapToBoundaries = () => {
  if (!leafletMap.current || boundaryPolygonsRef.current.length === 0) return;

  let bounds = null;
  boundaryPolygonsRef.current.forEach(polygon => {
    try {
      const polygonBounds = polygon.getBounds();
      if (polygonBounds.isValid()) {
        bounds = bounds ? bounds.extend(polygonBounds) : polygonBounds;
      }
    } catch (err) {
      console.warn("⚠️ Skipping invalid polygon during bounds fitting:", err);
    }
  });

  if (bounds && bounds.isValid()) {
    leafletMap.current.fitBounds(bounds, { padding: [20, 20], maxZoom: 18 });
  } else {
    console.warn("⚠️ Skipping fitBounds: no valid bounds calculated");
  }
};

// Function to remove markers by a list of APNs
const removeMarkersByAPNs = (apnList) => {
  console.log("Removing markers for APNs:", apnList);
  apnList.forEach(apn => {
    const marker = parcelMarkers.current[apn];
    if (marker && leafletMap.current && leafletMap.current.hasLayer(marker)) {
      leafletMap.current.removeLayer(marker);
      console.log(`Removed marker for APN: ${apn}`);
    }
    delete parcelMarkers.current[apn];
  });
  console.log("Remaining markers after removal:", Object.keys(parcelMarkers.current));
};


const removeMarkerAtIndex = (index) => {
  const marker = parcelMarkers.current[index];
  if (marker && leafletMap.current.hasLayer(marker)) {
    leafletMap.current.removeLayer(marker);
  }
  delete parcelMarkers.current[index];
};

useEffect(() => {
  if (!leafletMap.current) return;
  const onZoomStart = () => { zoomingRef.current = true; };
  const onZoomEnd   = () => { zoomingRef.current = false; };

  leafletMap.current.on('zoomstart', onZoomStart);
  leafletMap.current.on('zoomend', onZoomEnd);

  return () => {
    leafletMap.current.off('zoomstart', onZoomStart);
    leafletMap.current.off('zoomend', onZoomEnd);
  };
}, [leafletMap.current]);


useEffect(() => {
  return () => {
    if (heatmapSourceRef.current) {
      try { heatmapSourceRef.current.close(); } catch {}
      heatmapSourceRef.current = null;
    }
  };
}, []);

useEffect(() => {
  if (setRemoveMarkerAtIndexRef) {
    console.log("🧩 setRemoveMarkerAtIndexRef initialized");
    setRemoveMarkerAtIndexRef(removeMarkerAtIndex);
  }

}, []);

useEffect(() => {

  if (setRemoveMarkersByAPNsRef) {
    setRemoveMarkersByAPNsRef(() => removeMarkersByAPNs);
    console.log("removeMarkersByAPNs exposed to parent");
  }
}, [setRemoveMarkersByAPNsRef]);

useEffect(() => {
  Object.values(parcelMarkers.current).forEach(marker => {
  if (marker && leafletMap.current?.hasLayer(marker)) {
    leafletMap.current.removeLayer(marker);
  }
});

clearHeatmapOverlay();  
parcelMarkers.current = {};

  nextMarkerIndexRef.current = 0;
  didSetInitialPoint.current = false;

  fetch(`/api/geocode?area_name=${encodeURIComponent(areaName)}`)
    .then(async res => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.centroid) {
        throw new Error(data?.error || "Search location not found");
      }
      return data;
    })
    .then(data => {
      onSearchError?.("");
      const [lon, lat] = data.centroid;
      

      if (!leafletMap.current) {
        // const map = L.map(mapRef.current).setView([lat, lon], 13);
        const zoomLevel = areaName.toLowerCase().includes("usa") ? 4 : 13;

        // ✅ create the map here
        const map = L.map(mapRef.current).setView([lat, lon], zoomLevel);
        leafletMap.current = map;
        L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
          attribution: 'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
        }).addTo(map);
        L.tileLayer(
          "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
          {
            attribution: "Labels © Esri",
          }
        ).addTo(map);

        // Road labels overlay (street/road names)
        L.tileLayer(
          "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}",
          {
            attribution: "Roads © Esri",
          }
        ).addTo(map);
      }
        // ✅ If it's an APN, zoom to its polygon boundary instead of just centroid
      if (data.type === "apn" && data.boundary) {
        const latlngBoundary = data.boundary.map(([lon, lat]) => [lat, lon]);

        // Draw polygon + zoom
        const polygon = L.polygon(latlngBoundary, { color: "blue" }).addTo(leafletMap.current);
        if (!areaName.toLowerCase().includes("usa")) {
          leafletMap.current.fitBounds(polygon.getBounds());
        }
        // leafletMap.current.fitBounds(polygon.getBounds());

        // Store parcel so the rest of your app can use it
        // setBluePolygons(prev => [...prev, data.centroid]); // centroid is still [lon, lat]
        // setSelectedApns(prev => [...prev, data.apn]);

      //   window.dispatchEvent(new CustomEvent("parcelAdded", {
      //     detail: { apn: data.apn, address: data.address, boundary: data.boundary }
      //   })
      // );

      setSelectedApns(prev => {
        if (prev.includes(data.apn)) {
          console.log("Skipping duplicate parcel:", data.apn);
          return prev;
        }

        // ✅ add centroid only if APN was new
        setBluePolygons(bp => [...bp, data.centroid]); // centroid is still [lon, lat]

        // ✅ fire event only if APN was new
        window.dispatchEvent(new CustomEvent("parcelAdded", {
          detail: { apn: data.apn, address: data.address, boundary: data.boundary }
        }));

        return [...prev, data.apn];
      });

      } else {
        // leafletMap.current.setView([lat, lon], 13);
        if (areaName.toLowerCase().includes("usa")) {
          leafletMap.current.setView([lat, lon], 4);
        } else {
          leafletMap.current.setView([lat, lon], 13);
        }
      }

      leafletMap.current.off('click', handleParcelClick);
      if (!removeMode) {
        leafletMap.current.on('click', handleParcelClick);
      } else {
        // ensure click-to-add stays disabled after area changes while in Remove mode
        leafletMap.current.off('click', handleParcelClick);
      }
      // ✅ Only add marker if it hasn't been added yet
      if (!didSetInitialPoint.current && data.type === 'address') {
        const apn = data.apn
        const address = data.address
        const boundary = data.boundary
        console.log("📌 Adding initial center marker at:", lat, lon);
        // setBluePolygons(prev => [...prev, [lon, lat]]);
        didSetInitialPoint.current = true;
        
        if (boundary) {

            setSelectedApns(prev => {
              if (prev.includes(apn)) {
                console.log("Skipping duplicate parcel (address search):", apn);
                return prev;
              }

              setBluePolygons(bp => [...bp, [lon, lat]]);
              window.dispatchEvent(new CustomEvent("parcelAdded", {
                detail: { apn, address, boundary }
              }));

              return [...prev, apn];
            });
          // window.dispatchEvent(new CustomEvent("parcelAdded", {
          //   detail: { apn, address, boundary }
          // }));
        } else if (lat && lon) {
          // Fallback if no boundary returned
          // fetch(`/api/fetch_parcel/${lat}/${lon}`)
          //   .then(res => res.json())
          //   .then(bData => {
          //     window.dispatchEvent(new CustomEvent("parcelAdded", {
          //       detail: { apn: bData.apn, address: bData.address, boundary: bData.boundary}
          //     }));
          //   });

              fetch(`/api/fetch_parcel/${lat}/${lon}`)
            .then(res => res.json())
            .then(bData => {
              const apn2 = bData.apn;
              const address2 = bData.address;
              const boundary2 = bData.boundary;

              setSelectedApns(prev => {
                if (prev.includes(apn2)) {
                  console.log("Skipping duplicate parcel (fallback):", apn2);
                  return prev;
                }

                setBluePolygons(bp => [...bp, [lon, lat]]);
                window.dispatchEvent(new CustomEvent("parcelAdded", {
                  detail: { apn: apn2, address: address2, boundary: boundary2 }
                }));

                return [...prev, apn2];
              });
              })
        }
      }
    })
    .catch(err => {
      console.error("❌ Failed to fetch initial geocode:", err);
      onSearchError?.("Sorry, we couldn't find that location. Try some other way of accessing it.");
    });
}, [areaName]);


  useEffect(() => {
    const handlePanToCoords = (e) => {
      const { lat, lon } = e.detail;
      if (leafletMap.current) {
        leafletMap.current.setView([lat, lon], 15);
      }
    };

    window.addEventListener("panToCoords", handlePanToCoords);
    return () => window.removeEventListener("panToCoords", handlePanToCoords);
  }, []);

useEffect(() => {
  drawBoundaryPolygons(parcelBoundaries);
  fitMapToBoundaries();
}, [parcelBoundaries, selectedParcelIndex, removeMode, reportProgress]);

// DONT KNOE IF THIS IS RIGHT
// useEffect(() => {
//   drawBoundaryPolygons(parcelBoundaries);
//   fitMapToBoundaries();
// }, [parcelBoundaries, selectedParcelIndex, removeMode]);

const clearHeatmapOverlay = () => {
  if (heatmapOverlayRef.current && leafletMap.current && leafletMap.current.hasLayer(heatmapOverlayRef.current)) {
    leafletMap.current.removeLayer(heatmapOverlayRef.current);
    heatmapOverlayRef.current = null;
  }
    if (heatmapSourceRef.current) {
    try { heatmapSourceRef.current.close(); } catch {}
    heatmapSourceRef.current = null;
  }
  renderingRef.current = false;
  setHeatmapReady(false);
  window.setIsGeneratingHeatmaps(false);
};
// Then in your useEffect exposing the API:
useEffect(() => {
  if (mapApiRef) {
    mapApiRef.current = {
      renderHeatmapOverlay,
      clearHeatmapOverlay
    };
  }
}, [mapApiRef, renderHeatmapOverlay]);


// 3) TOP-LEVEL EFFECT: Clear or render when selection changes
useEffect(() => {
  const invalid =
    !Array.isArray(bluePolygons) ||
    bluePolygons.length === 0 ||
    (selectedParcelIndex != null && !bluePolygons[selectedParcelIndex]); // null means "combined", treat as valid

  if (invalid) clearHeatmapOverlay();
}, [bluePolygons, selectedParcelIndex]);

useEffect(() => {
  const formatted = formatHeatmapType(heatmapType);
  const layer = heatmapTypeLayerMap[formatted];

  // 1. Skip if layer doesn't exist
  if (!layer) {
    clearHeatmapOverlay();
    return;
  }

  // 2. Extract the part after underscore
  // heatmapType example: "soil_texture_class_30-60" or "soil_texture_class_null"
  if (heatmapType.startsWith('Soil_')){
  const depth = heatmapType.split('_')[1];

  // 3. If depth is "null" or empty → do not render heatmap
  if (!depth || depth === "null") {
    clearHeatmapOverlay();
    return;
  }
}

  // 4. Existing parcel-target check
  const hasTarget =
    bluePolygons.length > 0 &&
    (selectedParcelIndex == null || !!bluePolygons[selectedParcelIndex]);

  // 5. Render or clear
  if (hasTarget) renderHeatmapOverlay();
  else clearHeatmapOverlay();
}, [heatmapType]);


useEffect(() => {
  if (!leafletMap.current) return;

  leafletMap.current.off('click', handleParcelClick);   // remove if any
  if (!removeMode) {
    leafletMap.current.on('click', handleParcelClick);  // attach only in Select mode
  }
}, [removeMode, handleParcelClick]);

useEffect(() => {
  async function fetchUser() {
    try {
      const res = await fetch("/api/auth/status", {
        credentials: "include" // send cookies/session
      });
      const data = await res.json();
      if (data.authenticated && data.user) {
        setUsernameInput(data.user);
        console.log('map setUsernameInput(data.username);', setUsernameInput)
      }
    } catch (err) {
      console.error("Failed to fetch auth status:", err);
    }
  }
  fetchUser();
}, []);


return (
  <div className="relative w-full h-full">
    {/* Map container */}
    <div
      ref={mapRef}
      data-testid="parcel-base-map"
      role="region"
      aria-label="Parcel selection map"
      className="w-full h-full z-0"
      style={{ pointerEvents: 'auto' }}
    />

    {/* Floating Buttons */}
    <div className="absolute top-4 right-4 z-20 flex space-x-2">

      <button
        onClick={() => setMode('select')}
        className={`px-4 py-2 rounded-md shadow-md ${
          mode === 'select'
            ? "bg-green-600 hover:bg-green-700 text-white"
            : "bg-gray-200 hover:bg-gray-300 text-gray-700"
        }`}
        title="Click on the map to add/select sites"
      >
        Select
      </button>

      <button
        onClick={() =>
          setMode((prev) => (prev === 'remove' ? 'select' : 'remove'))
        }
        className={`px-4 py-2 rounded-md shadow-md ${
          removeMode
            ? "bg-red-600 hover:bg-red-700 text-white"
            : "bg-gray-200 hover:bg-gray-300 text-gray-700"
        }`}
        id="remove-btn"
        title="Click a site boundary to remove it"
      >
        {removeMode ? "Click Sites to Remove" : "Remove"}
      </button>

      <button
        onClick={async () => {
          if (reportProgress) return;
          try {
            if (bluePolygons.length > 0) {
            await fetch("/api/track_interaction", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                username: usernameInput,
                event_name: "analyze_btn",
                extra: JSON.stringify({
                  apn: selectedApns,
                }),
              }),
            });
          }
          } catch (err) {
            console.error("Failed to track analyze event:", err);
          }

          onGenerateReport(); // existing behavior
        }}
        disabled={reportProgress}
        className={`px-4 py-2 rounded-md shadow-md text-white transition-colors flex items-center gap-2 ${
          reportProgress
            ? "bg-blue-400 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700"
        }`}
        id="analyze-btn"
      >
        {reportProgress && (
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
        )}
        {reportProgress ? "Loading Preview..." : "Preview"}
      </button>
    </div>
  </div>
);
};

export default InteractiveMap;
